/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SelectChangeEvent } from '@mui/material';
import { MenuItem, Typography, Box } from '@mui/material';
import type { i18n, TFunction } from 'i18next';
import React from 'react';
import {
  AlertIcon,
  CustomTooltip,
  SearchField,
  SearchHighlight,
  TooltipSelect,
} from '@opengeoweb/shared';
import type { LayerProps } from '@opengeoweb/webmap';
import { useCoreTranslation } from '../../../../../utils/i18n';

export const showNoLayers = (
  t: TFunction<'core', i18n>,
): React.ReactElement => {
  return (
    <Box
      sx={{
        paddingLeft: 1,
        height: '32px',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      <Typography
        sx={{
          fontSize: (theme): number | string =>
            theme.palette.geowebColors.layerManager.tableRowDefaultText
              .fontSize!,
          color: 'geowebColors.layerManager.tableRowDefaultText.rgba',
          fontWeight: 500,
        }}
        variant="body1"
      >
        {t('layermanager-baselayers-no-available')}
      </Typography>
    </Box>
  );
};

export interface RenderLayersProps {
  onChangeLayerName: (name: string) => void;
  layerName: string;
  layers: LayerProps[];
  isEnabled?: boolean;
  tooltipPrefix?: string;
  skipLocalStyling?: boolean;
}

const getRenderLayersValues = (
  layerName: string,
  layers: LayerProps[],
): {
  currentValue: string;
  currentIndex: number;
  extendedLayers: LayerProps[];
} => {
  const serviceLayer = layers.find((layer) => layer.name === layerName);
  if (!serviceLayer && layerName) {
    return {
      currentValue: layerName,
      currentIndex: layers.length,
      extendedLayers: [
        ...layers,
        {
          name: layerName,
          title: layerName,
          leaf: true,
          path: [],
        },
      ],
    };
  }
  return {
    currentValue: serviceLayer?.name || '',
    currentIndex: layers.findIndex(
      (layer) => layer.name === serviceLayer?.name,
    ),
    extendedLayers: layers,
  };
};

const RenderLayers: React.FC<RenderLayersProps> = ({
  onChangeLayerName,
  layerName,
  layers = [],
  isEnabled = true,
  tooltipPrefix,
  skipLocalStyling,
}: RenderLayersProps) => {
  const [searchFilter, setSearchFilter] = React.useState('');
  const { t } = useCoreTranslation();
  const nonEmptyTooltipPrefix = tooltipPrefix ?? `${t('layermanager-layer')}: `;

  if (!layers?.length) {
    return showNoLayers(t);
  }

  const selectLayer = (event: SelectChangeEvent<unknown>): void => {
    event.stopPropagation();
    onChangeLayerName(event.target.value as string);
  };

  const { currentIndex, currentValue, extendedLayers } = getRenderLayersValues(
    layerName,
    layers,
  );

  const list = extendedLayers.map((layer) => ({
    value: layer.name!,
  }));

  const filteredLayers = extendedLayers.filter((layer) =>
    layer.title?.toLowerCase().includes(searchFilter.toLowerCase()),
  );

  return (
    <TooltipSelect
      MenuProps={{
        slotProps: {
          paper: {
            style: {
              width: '500px',
            },
          },
          list: {
            style: {
              padding: 0,
            },
          },
        },
      }}
      currentIndex={currentIndex}
      disableUnderline
      inputProps={{
        SelectDisplayProps: {
          'data-testid': 'selectLayer',
        },
      }}
      isEnabled={isEnabled}
      list={list}
      onChange={selectLayer}
      onChangeMouseWheel={(e): void => onChangeLayerName(e.value)}
      // Omit possible warning sign in the selected item due to layout effects
      renderValue={(value): string =>
        extendedLayers.find((layer): boolean => layer.name === value)!.title!
      }
      skipLocalStyling={skipLocalStyling}
      tooltip={`${nonEmptyTooltipPrefix}${extendedLayers[currentIndex]?.title}`}
      value={currentValue}
    >
      <Box
        ref={(node: HTMLDivElement | null): void => {
          if (node) {
            // focus the search field when the menu opens
            requestAnimationFrame(() => {
              node.querySelector<HTMLInputElement>('input')?.focus();
            });
          }
        }}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: 'geowebColors.background.surface',
        }}
      >
        <SearchField
          searchFilter={searchFilter}
          setSearchFilter={setSearchFilter}
        />
      </Box>
      <MenuItem disabled>
        {filteredLayers.length
          ? t('layermanager-layer')
          : `${t('layermanager-layer')} - ${t('search-no-results')}`}
      </MenuItem>
      {filteredLayers.map(
        (layerFromServiceLayers: LayerProps, currentIndex) => (
          <MenuItem
            disabled={!layerFromServiceLayers.leaf!} // show categories as disabled
            key={layerFromServiceLayers.name}
            sx={{
              '& .MuiSvgIcon-root': { marginLeft: '28px' },
              gap: 1,
            }}
            value={layerFromServiceLayers.name!}
          >
            <CustomTooltip title={layerFromServiceLayers.title!}>
              <Box
                component="span"
                sx={{
                  display: 'block',
                  flex: '1 1 auto',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <SearchHighlight
                  search={searchFilter}
                  text={layerFromServiceLayers.title!}
                />
              </Box>
            </CustomTooltip>
            {currentIndex === layers.length && <AlertIcon severity="error" />}
          </MenuItem>
        ),
      )}
    </TooltipSelect>
  );
};

export default RenderLayers;
